import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card } from '../components/ui/card';
import { Slider } from '../components/ui/slider';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet';
import { 
  SlidersHorizontal, Loader2,
  ChevronRight, ChevronDown, Home, X
} from 'lucide-react';
import ListingCard from '../components/ListingCard';
import CityAutocomplete from '../components/CityAutocomplete';
import VehicleFilters from '../components/VehicleFilters';
import { ServicesSection, SportsServicesSection } from '../components/services';
import JewelleryFinanceServices from '../components/services/JewelleryFinanceServices';
import TradesServicesSection, { EmergencyServicesBanner, FeaturedTradeProviders } from '../components/services/TradesServicesSection';
import SubcategoryServicesDisplay, { AllSubcategoriesServicesDisplay } from '../components/services/SubcategoryServicesDisplay';
import BusinessServicesDisplay from '../components/services/BusinessServicesDisplay';
import FoodAgricultureServicesDisplay from '../components/services/FoodAgricultureServicesDisplay';
import WhatsInSeasonSection from '../components/services/WhatsInSeasonSection';
import EducationTuitionServicesDisplay from '../components/services/EducationTuitionServicesDisplay';
import BabyKidsServicesDisplay from './services/BabyKidsServicesDisplay';
import EventProvidersSection, { isEventServiceCategory } from '../components/services/EventProvidersSection';
import CommunityServicesSection, { isCommunityCategory } from '../components/services/CommunityServicesSection';
import BooksMediaServicesSection, { isBooksMediaCategory } from '../components/services/BooksMediaServicesSection';
import CommunityLocalLifeSection from '../components/community/CommunityLocalLifeSection';
import { RFQEntryButton } from '../components/services/RFQFlow';
import { useCountry } from '../context/CountryContext';
import { isVehicleCategory } from '../config/vehicleFilterSchemas';
import PropertyFilters, { isPropertyCategory } from '../components/PropertyFilters';
import PropertyMapView from '../components/PropertyMapView';
import PropertyHeatMap from '../components/PropertyHeatMap';
import DrawableMap from '../components/DrawableMap';
import RecentSearches, { saveRecentSearch } from '../components/RecentSearches';
import CompareProperties, { CompareButton, CompareBar } from '../components/CompareProperties';
import useSearchHistorySync from '../hooks/useSearchHistorySync';
import usePropertySearch from '../hooks/usePropertySearch';
import PropertySearchResults from '../components/PropertySearchResults';

// Import modular category components
import { Breadcrumbs, ActiveFilters } from '../components/category/CategoryFilters';
import { 
  ListingsLoading, 
  ListingsError, 
  ListingsEmpty, 
  FeaturedAdsSection, 
  RegularAdsSection 
} from '../components/category/CategoryListings';
import CategoryToolbar from '../components/category/CategoryToolbar';
import CategoryServicesRenderer from '../components/category/CategoryServicesRenderer';
import { CategoryFilter } from '../components/category/CategorySidebar';
import CategoryHeader from '../components/category/CategoryHeader';
import MobileFilterSheet from '../components/category/MobileFilterSheet';
import SortSelector from '../components/category/SortSelector';
import ViewModeToggle from '../components/category/ViewModeToggle';

// Import category helper utilities
import {
  isFashionCategory,
  isClothingCategory,
  isJewelleryCategory,
  isSportsCategory,
  isServicesTradesCategory,
  isBusinessIndustryCategory,
  isFoodAgricultureCategory,
  isEducationCategory,
  isBabyKidsCategory,
  isPetsAnimalsCategory,
  isMusicalInstrumentsCategory,
  isRFQEnabledBusiness,
  isRFQEnabledFoodAgri,
  getVehicleType as getVehicleTypeFromSlug,
  slugToDisplayName,
  getMusicSubcategoryMap,
  getTradesSubcategoryMap,
  getBusinessSubcategoryMap,
  getFoodAgriSubcategoryMap,
  getSportsSubcategoryMap,
  getFashionServiceCategory,
  isJobsCategory,
  getCategoryDisplayName,
} from '../utils/CategoryHelpers';

import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CategoryPage = () => {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get('q') || '';
  const subParam = searchParams.get('sub') || ''; // Get subcategory from URL
  const navigate = useNavigate();
  const { country, countryConfig, formatCurrency } = useCountry();
  
  const [viewMode, setViewMode] = useState('grid');
  const [sortBy, setSortBy] = useState('recent');
  const [selectedCategory, setSelectedCategory] = useState(slug === 'all' ? '' : slug);
  const [selectedSubcategory, setSelectedSubcategory] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [priceRange, setPriceRange] = useState([0, 10000000]);
  const [condition, setCondition] = useState('all');
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Categories state
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [expandedCategories, setExpandedCategories] = useState({});
  
  // Current category info for breadcrumb
  const [currentCategory, setCurrentCategory] = useState(null);
  const [parentCategory, setParentCategory] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  
  // Vehicle filters state
  const [vehicleFilters, setVehicleFilters] = useState({});
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  
  // Property filters state
  const [propertyFilters, setPropertyFilters] = useState({});
  const [propertyViewMode, setPropertyViewMode] = useState('list'); // 'list', 'map', 'draw', 'heat'
  
  // Compare properties state
  const [compareIds, setCompareIds] = useState([]);
  const [showCompareModal, setShowCompareModal] = useState(false);
  const MAX_COMPARE = 4;
  
  // Search history sync hook
  const { isLoggedIn, isSyncing, addSearch, syncToServer } = useSearchHistorySync();
  
  // Check if current category is a vehicle category
  const isVehicle = useMemo(() => isVehicleCategory(slug), [slug]);
  
  // Check if current category is a property category
  const isProperty = useMemo(() => isPropertyCategory(slug), [slug]);
  
  // Use the property search hook for property categories
  const {
    propertyFilters: hookPropertyFilters,
    propertyViewMode: hookPropertyViewMode,
    compareIds: hookCompareIds,
    showCompareModal: hookShowCompareModal,
    hoverZoomSettings,
    activeFilters: hookActiveFilters,
    setPropertyFilters: setHookPropertyFilters,
    setPropertyViewMode: setHookPropertyViewMode,
    setShowCompareModal: setHookShowCompareModal,
    handleCompareToggle: hookHandleCompareToggle,
    clearCompare: hookClearCompare,
    removeFilter: hookRemoveFilter,
    clearAllFilters: hookClearAllFilters,
    MAX_COMPARE: HOOK_MAX_COMPARE
  } = usePropertySearch({}, country);
  
  // Use hook values for property categories, fallback to local state for others
  const effectivePropertyFilters = isProperty ? hookPropertyFilters : propertyFilters;
  const effectivePropertyViewMode = isProperty ? hookPropertyViewMode : propertyViewMode;
  const effectiveCompareIds = isProperty ? hookCompareIds : compareIds;
  const effectiveShowCompareModal = isProperty ? hookShowCompareModal : showCompareModal;
  const effectiveMaxCompare = isProperty ? HOOK_MAX_COMPARE : MAX_COMPARE;
  
  // Compare toggle handler - use hook for property, local for others
  const handleCompareToggle = useCallback((listingId) => {
    if (isProperty) {
      hookHandleCompareToggle(listingId);
    } else {
      setCompareIds(prev => {
        if (prev.includes(listingId)) {
          return prev.filter(id => id !== listingId);
        }
        if (prev.length >= MAX_COMPARE) {
          return prev;
        }
        return [...prev, listingId];
      });
    }
  }, [isProperty, hookHandleCompareToggle]);
  
  // Use imported helper functions for category detection
  const isFashion = useMemo(() => isFashionCategory(slug), [slug]);
  const isClothing = useMemo(() => isClothingCategory(slug), [slug]);
  const isJewellery = useMemo(() => isJewelleryCategory(slug), [slug]);
  const isSports = useMemo(() => isSportsCategory(slug), [slug]);
  const isServicesTrades = useMemo(() => isServicesTradesCategory(slug), [slug]);
  const isBusinessIndustry = useMemo(() => isBusinessIndustryCategory(slug), [slug]);
  const isFoodAgriculture = useMemo(() => isFoodAgricultureCategory(slug), [slug]);
  const isEducation = useMemo(() => isEducationCategory(slug), [slug]);
  const isBabyKids = useMemo(() => isBabyKidsCategory(slug), [slug]);
  const isPetsAnimals = useMemo(() => isPetsAnimalsCategory(slug), [slug]);
  const isMusicalInstruments = useMemo(() => isMusicalInstrumentsCategory(slug), [slug]);
  
  // Check if this is an event service category
  const isEventService = useMemo(() => isEventServiceCategory(slug), [slug]);
  
  // Check if this is a community category
  const isCommunity = useMemo(() => isCommunityCategory(slug), [slug]);
  
  // Check if this is the main Community & Local Life parent category
  const isCommunityLocalLifeParent = useMemo(() => slug === 'community-local-life', [slug]);
  
  // RFQ enabled checks
  const isRFQEnabledSubcategory = useMemo(() => isRFQEnabledBusiness(slug), [slug]);
  const isFoodAgriRFQEnabled = useMemo(() => isRFQEnabledFoodAgri(slug), [slug]);
  
  // Get vehicle type
  const getVehicleType = () => getVehicleTypeFromSlug(slug);

  // Helper functions using centralized mappings
  const getServiceCategory = () => getFashionServiceCategory(slug);

  const getSportsSubcategory = () => {
    if (subParam) return subParam;
    if (selectedSubcategory) return selectedSubcategory;
    const subcategoryMap = getSportsSubcategoryMap();
    return subcategoryMap[slug?.toLowerCase()] || 'sports-equipment';
  };

  const getTradesSubcategory = () => {
    if (subParam) return subParam;
    if (selectedSubcategory) return selectedSubcategory;
    const subcategoryMap = getTradesSubcategoryMap();
    return subcategoryMap[slug?.toLowerCase()];
  };

  const getBusinessSubcategory = () => {
    if (subParam) return subParam;
    if (selectedSubcategory) return selectedSubcategory;
    const subcategoryMap = getBusinessSubcategoryMap();
    return subcategoryMap[slug?.toLowerCase()];
  };

  const getFoodAgriSubcategory = () => {
    if (subParam) return subParam;
    if (selectedSubcategory) return selectedSubcategory;
    const subcategoryMap = getFoodAgriSubcategoryMap();
    return subcategoryMap[slug?.toLowerCase()];
  };

  const getMusicSubcategory = () => {
    if (subParam) return subParam;
    if (selectedSubcategory) return selectedSubcategory;
    const subcategoryMap = getMusicSubcategoryMap();
    return subcategoryMap[slug?.toLowerCase()];
  };

  // Check if current category is a Jobs category - use centralized helper
  const isJobs = useMemo(() => isJobsCategory(slug), [slug]);
  
  // Redirect to Jobs page if it's a Jobs category
  useEffect(() => {
    if (isJobs) {
      navigate(slug === 'jobs' ? '/jobs' : `/jobs?category=${slug}`);
    }
  }, [isJobs, slug, navigate]);

  // Fetch categories with counts
  useEffect(() => {
    fetchCategories();
  }, []);

  // Redirect to dedicated Books & Media page for books-media categories
  useEffect(() => {
    const booksMediaCategories = ['books', 'magazines', 'educational-media', 'comics-graphic-novels', 'cds-dvds', 'digital-media', 'collectibles', 'stationery'];
    
    if (slug && booksMediaCategories.includes(slug.toLowerCase())) {
      // Redirect to the dedicated books-media page with category filter
      navigate(`/books-media?category=${slug}`, { replace: true });
      return;
    }
    
    // Also check if parent category is books-media
    if (slug === 'books-media') {
      navigate('/books-media', { replace: true });
      return;
    }
  }, [slug, navigate]);

  // Update current category when slug changes
  useEffect(() => {
    if (categories.length > 0 && slug && slug !== 'all') {
      findCurrentCategory(slug);
    } else {
      setCurrentCategory(null);
      setParentCategory(null);
    }
    // Reset vehicle filters when category changes
    setVehicleFilters({});
    // Reset property filters when category changes
    setPropertyFilters({});
  }, [slug, categories]);

  // Fetch listings when filters change (non-vehicle categories)
  useEffect(() => {
    if (!isVehicle && !isProperty) {
      fetchListings();
    }
  }, [selectedCategory, selectedSubcategory, selectedLocation, condition, priceRange, sortBy, searchQuery, slug, subParam, isVehicle, isProperty]);
  
  // Initial fetch for vehicle categories
  useEffect(() => {
    if (isVehicle) {
      fetchListings();
    }
  }, [slug, isVehicle]);
  
  // Fetch listings for property category with property filters
  useEffect(() => {
    if (isProperty) {
      fetchListings(null, propertyFilters);
    }
  }, [slug, isProperty, subParam, JSON.stringify(propertyFilters)]);

  // Handle vehicle filter changes
  const handleVehicleFilterChange = (filters) => {
    setVehicleFilters(filters);
  };

  // Handle vehicle filter search
  const handleVehicleSearch = (filters) => {
    setVehicleFilters(filters);
    setMobileFilterOpen(false);
    fetchListings(filters);
  };
  
  // Handle property filter changes
  const handlePropertyFilterChange = (filters) => {
    // Use hook setter for property categories, local state otherwise
    if (isProperty) {
      setHookPropertyFilters(filters);
    } else {
      setPropertyFilters(filters);
    }
    
    // Save to recent searches if filters have meaningful values
    const hasFilters = filters.location || 
      filters.minPrice || 
      filters.maxPrice || 
      (filters.bedrooms && filters.bedrooms !== 'any') ||
      (filters.propertyType && filters.propertyType !== 'any');
    
    if (hasFilters) {
      // Use the sync-enabled addSearch (saves locally + server if logged in)
      addSearch(filters, totalCount);
    }
  };

  const fetchCategories = async () => {
    try {
      setCategoriesLoading(true);
      const response = await axios.get(`${API_URL}/api/categories/`);
      setCategories(response.data.categories || []);
    } catch (err) {
      console.error('Error fetching categories:', err);
    } finally {
      setCategoriesLoading(false);
    }
  };

  const findCurrentCategory = (categorySlug) => {
    // First check main categories
    const mainCat = categories.find(c => c.slug === categorySlug);
    if (mainCat) {
      setCurrentCategory(mainCat);
      setParentCategory(null);
      setSelectedCategory(categorySlug);
      setSelectedSubcategory('');
      // Auto-expand this category
      setExpandedCategories(prev => ({ ...prev, [categorySlug]: true }));
      return;
    }
    
    // Check subcategories
    for (const cat of categories) {
      if (cat.subcategories) {
        const subCat = cat.subcategories.find(s => s.slug === categorySlug);
        if (subCat) {
          setCurrentCategory(subCat);
          setParentCategory(cat);
          setSelectedCategory(cat.slug);
          setSelectedSubcategory(categorySlug);
          // Auto-expand parent category
          setExpandedCategories(prev => ({ ...prev, [cat.slug]: true }));
          return;
        }
      }
    }
  };

  const fetchListings = async (overrideFilters = null, propFilters = null) => {
    try {
      setLoading(true);
      
      const params = new URLSearchParams();
      
      // Use the slug from URL or selected category
      const categoryToUse = slug && slug !== 'all' ? slug : selectedCategory;
      
      // Check if this is a Services & Trades subcategory
      const tradesSubcategories = [
        'home-repairs-maintenance', 'plumbing-electrical', 'construction-masonry',
        'cleaning-services', 'moving-removal', 'pest-control', 'security-services',
        'tailoring-alterations', 'ac-refrigeration', 'solar-power', 'water-pumps',
        'aluminium-glass', 'welding-fabrication'
      ];
      
      if (tradesSubcategories.includes(categoryToUse)) {
        // For trade subcategories, use parent category and subcategory filter
        params.append('category', 'services-trades');
        params.append('subcategory', categoryToUse);
      } else if (categoryToUse) {
        params.append('category', categoryToUse);
      }
      
      // Handle subcategory filter from URL param (e.g., ?sub=vehicle-insurance)
      const subcategoryToUse = subParam || selectedSubcategory;
      if (subcategoryToUse) {
        params.append('subcategory', subcategoryToUse);
      }
      
      // Use override filters if provided (from vehicle filters), otherwise use state
      const filtersToUse = overrideFilters || vehicleFilters;
      
      // Property-specific filters
      const propertyFiltersToUse = propFilters || propertyFilters;
      if (isProperty && propertyFiltersToUse) {
        // Location from property filters
        if (propertyFiltersToUse.location) {
          params.append('location', propertyFiltersToUse.location);
        }
        // Price range
        if (propertyFiltersToUse.minPrice) {
          params.append('min_price', propertyFiltersToUse.minPrice);
        }
        if (propertyFiltersToUse.maxPrice) {
          params.append('max_price', propertyFiltersToUse.maxPrice);
        }
        // Bedrooms
        if (propertyFiltersToUse.bedrooms && propertyFiltersToUse.bedrooms !== 'any') {
          params.append('bedrooms', propertyFiltersToUse.bedrooms);
        }
        // Property type
        if (propertyFiltersToUse.propertyType && propertyFiltersToUse.propertyType !== 'any') {
          params.append('property_type', propertyFiltersToUse.propertyType);
        }
        // Multiple property types
        if (propertyFiltersToUse.propertyTypes?.length > 0) {
          params.append('property_types', propertyFiltersToUse.propertyTypes.join(','));
        }
        // Furnishing
        if (propertyFiltersToUse.furnishing && propertyFiltersToUse.furnishing !== 'any') {
          params.append('furnishing', propertyFiltersToUse.furnishing);
        }
        // Available from
        if (propertyFiltersToUse.availableFrom) {
          params.append('available_from', propertyFiltersToUse.availableFrom);
        }
        // Include let agreed
        if (propertyFiltersToUse.includeLetAgreed) {
          params.append('include_let_agreed', 'true');
        }
        // Part of house (SL specific)
        if (propertyFiltersToUse.partOfHouse && propertyFiltersToUse.partOfHouse !== 'any') {
          params.append('part_of_house', propertyFiltersToUse.partOfHouse);
        }
        // Features
        if (propertyFiltersToUse.features) {
          const activeFeatures = Object.entries(propertyFiltersToUse.features)
            .filter(([_, enabled]) => enabled)
            .map(([id]) => id);
          if (activeFeatures.length > 0) {
            params.append('features', activeFeatures.join(','));
          }
        }
        // Country-specific features
        if (propertyFiltersToUse.countryFeatures) {
          const activeCountryFeatures = Object.entries(propertyFiltersToUse.countryFeatures)
            .filter(([_, enabled]) => enabled)
            .map(([id]) => id);
          if (activeCountryFeatures.length > 0) {
            params.append('country_features', activeCountryFeatures.join(','));
          }
        }
        // Note: sort_by is handled at the end of the function to avoid duplicates
      }
      
      // Location - check both standard and vehicle filter (non-property)
      if (!isProperty) {
        const locationToUse = filtersToUse.location || selectedLocation;
        if (locationToUse && locationToUse.trim()) {
          params.append('location', locationToUse);
        }
      }
      
      // Condition - check both standard and vehicle filter
      const conditionToUse = filtersToUse.condition || condition;
      if (conditionToUse && conditionToUse !== 'all') {
        params.append('condition', conditionToUse);
      }
      
      // Price range - check both standard and vehicle filter (non-property)
      if (!isProperty) {
        const priceMinToUse = filtersToUse.priceMin || priceRange[0];
        const priceMaxToUse = filtersToUse.priceMax || priceRange[1];
        if (priceMinToUse) {
          params.append('min_price', priceMinToUse);
        }
        if (priceMaxToUse && priceMaxToUse < 10000000) {
          params.append('max_price', priceMaxToUse);
        }
      }
      
      if (searchQuery) {
        params.append('search', searchQuery);
      }
      
      // Vehicle-specific filters
      if (filtersToUse.make) {
        params.append('make', filtersToUse.make);
      }
      if (filtersToUse.model) {
        params.append('model', filtersToUse.model);
      }
      if (filtersToUse.yearMin) {
        params.append('year_min', filtersToUse.yearMin);
      }
      if (filtersToUse.yearMax) {
        params.append('year_max', filtersToUse.yearMax);
      }
      if (filtersToUse.mileageMin) {
        params.append('mileage_min', filtersToUse.mileageMin);
      }
      if (filtersToUse.mileageMax) {
        params.append('mileage_max', filtersToUse.mileageMax);
      }
      
      // Multi-select filters (arrays to comma-separated)
      if (filtersToUse.fuelType?.length > 0) {
        params.append('fuel_type', filtersToUse.fuelType.join(','));
      }
      if (filtersToUse.transmission?.length > 0) {
        params.append('transmission', filtersToUse.transmission.join(','));
      }
      if (filtersToUse.bodyType?.length > 0) {
        params.append('body_type', filtersToUse.bodyType.join(','));
      }
      if (filtersToUse.driveType?.length > 0) {
        params.append('drive_type', filtersToUse.driveType.join(','));
      }
      if (filtersToUse.doors?.length > 0) {
        params.append('doors', filtersToUse.doors.join(','));
      }
      if (filtersToUse.seats?.length > 0) {
        params.append('seats', filtersToUse.seats.join(','));
      }
      if (filtersToUse.colour?.length > 0) {
        params.append('colour', filtersToUse.colour.join(','));
      }
      
      // Boolean filters
      if (filtersToUse.verifiedSeller) {
        params.append('verified_seller', 'true');
      }
      if (filtersToUse.inspectionAvailable) {
        params.append('inspection_available', 'true');
      }
      
      // Vehicle type specific filters
      if (filtersToUse.bikeType?.length > 0) {
        params.append('bike_type', filtersToUse.bikeType.join(','));
      }
      if (filtersToUse.engineCCBand?.length > 0) {
        params.append('engine_cc_band', filtersToUse.engineCCBand.join(','));
      }
      if (filtersToUse.boatType?.length > 0) {
        params.append('boat_type', filtersToUse.boatType.join(','));
      }
      if (filtersToUse.equipmentType?.length > 0) {
        params.append('equipment_type', filtersToUse.equipmentType.join(','));
      }
      
      // Sort - use property filter sort for property, otherwise use global sort
      const effectiveSortBy = isProperty && propertyFiltersToUse?.sortBy 
        ? propertyFiltersToUse.sortBy 
        : sortBy;
      params.append('sort_by', effectiveSortBy);
      params.append('limit', 50);
      
      console.log('Params before request:', params.toString());
      
      // Enable AI ranking for relevance sort or when preset with ranking weights is applied
      const hasPresetRankingWeights = filtersToUse._rankingWeights && Object.keys(filtersToUse._rankingWeights).length > 0;
      
      if (effectiveSortBy === 'relevance' || hasPresetRankingWeights) {
        params.append('enable_ai_ranking', 'true');
        
        // Pass preset ranking weights if available
        if (hasPresetRankingWeights) {
          params.append('preset_id', filtersToUse._presetId || '');
          params.append('preset_ranking_weights', JSON.stringify(filtersToUse._rankingWeights));
        }
      }
      
      // Always pass country for proper data transformation (only once)
      params.append('country_code', country);
      
      const response = await axios.get(`${API_URL}/api/listings/?${params.toString()}`);
      
      setListings(response.data.listings || []);
      setTotalCount(response.data.total || response.data.listings?.length || 0);
      setError(null);
    } catch (err) {
      console.error('Error fetching listings:', err);
      setError('Failed to load listings');
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryClick = (catSlug, isSubcategory = false, parentSlug = null) => {
    if (isSubcategory && parentSlug) {
      // Navigate to parent category with subcategory filter
      navigate(`/category/${parentSlug}?sub=${catSlug}`);
    } else if (isSubcategory && currentCategory) {
      // If we're already on a category page and clicking a subcategory
      navigate(`/category/${currentCategory.slug}?sub=${catSlug}`);
    } else {
      // Regular category navigation
      navigate(`/category/${catSlug}`);
    }
  };

  const toggleCategoryExpand = (catSlug) => {
    setExpandedCategories(prev => ({
      ...prev,
      [catSlug]: !prev[catSlug]
    }));
  };

  const clearFilters = () => {
    setSelectedCategory('');
    setSelectedSubcategory('');
    setSelectedLocation('');
    setCondition('all');
    setPriceRange([0, 10000000]);
    navigate('/category/all');
  };

  // Get display name for the page title - uses local state for context
  const getPageDisplayName = () => {
    if (searchQuery) return `Search: "${searchQuery}"`;
    if (currentCategory) return currentCategory.name;
    if (slug === 'all' || !slug) return 'All Ads';
    // Use the centralized category name helper
    return getCategoryDisplayName(slug);
  };

  // Render the category filter using the extracted component
  const renderCategoryFilter = () => {
    // If we're on a parent category that has subcategories, show those instead
    if (currentCategory && !parentCategory && currentCategory.subcategories?.length > 0) {
      // We're on a main category with subcategories - show subcategory navigation
      return (
        <div className="space-y-2 min-h-[350px] transition-all duration-200" data-testid="subcategory-filter">
          <Label className="mb-2 block font-semibold text-gray-700">{currentCategory.name}</Label>

          {/* Current category - show all */}
          <div
            onClick={() => {
              setSelectedSubcategory('');
              navigate(`/category/${currentCategory.slug}`);
            }}
            className={`flex items-center justify-between p-1.5 rounded-md cursor-pointer transition-all duration-150 ${
              !selectedSubcategory && !subParam
                ? 'bg-blue-100 border border-blue-500'
                : 'hover:bg-gray-50 border border-transparent'
            }`}
            data-testid={`category-${currentCategory.slug}-all`}
          >
            <div className="flex items-center gap-2">
              <span className="text-base leading-none">{currentCategory.icon}</span>
              <span className="text-xs font-medium">All {currentCategory.name}</span>
            </div>
            <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
              {totalCount || 0}
            </Badge>
          </div>

          {/* Subcategories list */}
          <div className="max-h-[55vh] overflow-y-auto space-y-0.5 pr-1 ml-2 border-l-2 border-gray-200 pl-3 scroll-smooth">
            {currentCategory.subcategories.map((subcat) => (
              <div
                key={subcat.slug}
                onClick={() => {
                  setSelectedSubcategory(subcat.slug);
                  navigate(`/category/${currentCategory.slug}?sub=${subcat.slug}`);
                }}
                className={`flex items-center gap-2 p-1.5 rounded-md cursor-pointer transition-all duration-150 ${
                  selectedSubcategory === subcat.slug || subParam === subcat.slug
                    ? 'bg-blue-50 border border-blue-300 font-medium'
                    : 'hover:bg-gray-100 border border-transparent'
                }`}
                data-testid={`subcategory-${subcat.slug}`}
              >
                {subcat.icon && <span className="text-sm leading-none">{subcat.icon}</span>}
                <span className="text-xs text-gray-700">{subcat.name}</span>
              </div>
            ))}
          </div>
        </div>
      );
    }
    
    // Default: show all categories with min-height to prevent layout shift
    return (
      <div className="min-h-[350px] transition-all duration-200">
        <CategoryFilter
          categories={categories}
          slug={slug}
          selectedCategory={selectedCategory}
          selectedSubcategory={selectedSubcategory}
          expandedCategories={expandedCategories}
          handleCategoryClick={handleCategoryClick}
          toggleCategoryExpand={toggleCategoryExpand}
        />
      </div>
    );
  };

  const FilterSection = () => (
    <div className="space-y-6">
      {/* Categories with Subcategories - with loading skeleton */}
      <div className="min-h-[350px]">
        {categoriesLoading ? (
          <div className="space-y-2 animate-pulse" data-testid="category-loading-skeleton">
            <div className="h-5 w-24 bg-gray-200 rounded mb-3"></div>
            <div className="h-10 bg-gray-200 rounded"></div>
            {[1,2,3,4,5,6,7,8].map(i => (
              <div key={i} className="flex items-center justify-between p-2">
                <div className="flex items-center gap-2">
                  <div className="h-6 w-6 bg-gray-200 rounded"></div>
                  <div className="h-4 w-28 bg-gray-200 rounded"></div>
                </div>
                <div className="h-5 w-8 bg-gray-200 rounded"></div>
              </div>
            ))}
          </div>
        ) : (
          renderCategoryFilter()
        )}
      </div>
      
      <div className="border-t pt-4">
        <Label className="mb-2 block">Location</Label>
        <CityAutocomplete
          value={selectedLocation}
          onChange={setSelectedLocation}
          placeholder="Type nearest city"
        />
      </div>

      <div>
        <Label className="mb-2 block">Condition</Label>
        <Select value={condition} onValueChange={setCondition}>
          <SelectTrigger>
            <SelectValue placeholder="All Conditions" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Conditions</SelectItem>
            <SelectItem value="Brand New">Brand New</SelectItem>
            <SelectItem value="Like New">Like New</SelectItem>
            <SelectItem value="Used">Used</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label className="mb-3 block">Price Range ({countryConfig?.currency_symbol || 'Rs.'})</Label>
        <Slider
          value={priceRange}
          onValueChange={setPriceRange}
          max={10000000}
          step={100000}
          className="mb-2"
        />
        <div className="flex justify-between text-sm text-gray-500">
          <span>{formatCurrency(priceRange[0])}</span>
          <span>{formatCurrency(priceRange[1])}</span>
        </div>
      </div>

      <Button variant="outline" className="w-full" onClick={clearFilters}>
        <X className="h-4 w-4 mr-2" />
        Reset Filters
      </Button>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header with Breadcrumbs */}
      <CategoryHeader
        slug={slug}
        pageTitle={getPageDisplayName()}
        isCommunity={isCommunity}
        getCategoryDisplayName={getCategoryDisplayName}
      />

      {/* Property Filters (Rightmove/Zoopla style) */}
      {isProperty && (
        <PropertyFilters
          onFiltersChange={handlePropertyFilterChange}
          initialFilters={propertyFilters}
          resultCount={totalCount}
          viewMode={propertyViewMode}
          onViewModeChange={setPropertyViewMode}
        />
      )}

      {/* Property Subcategory Navigation */}
      {isProperty && (
        <div className="bg-white border-b shadow-sm min-h-[52px]">
          <div className="max-w-7xl mx-auto px-4 py-3">
            {currentCategory && currentCategory.subcategories?.length > 0 ? (
              <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide">
                <button
                  onClick={() => {
                    setSelectedSubcategory('');
                    navigate(`/category/${currentCategory.slug}`);
                  }}
                  className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    !subParam && !selectedSubcategory
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  data-testid="property-subcategory-all"
                >
                  All Property
                </button>
                {currentCategory.subcategories.map((subcat) => (
                  <button
                    key={subcat.slug}
                    onClick={() => {
                      setSelectedSubcategory(subcat.slug);
                      navigate(`/category/${currentCategory.slug}?sub=${subcat.slug}`);
                    }}
                    className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                      subParam === subcat.slug || selectedSubcategory === subcat.slug
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                    data-testid={`property-subcategory-${subcat.slug}`}
                  >
                    {subcat.name}
                  </button>
                ))}
              </div>
            ) : categoriesLoading ? (
              <div className="flex items-center gap-2 animate-pulse">
                {[1,2,3,4,5,6].map(i => (
                  <div key={i} className="h-9 w-28 bg-gray-200 rounded-full"></div>
                ))}
              </div>
            ) : null}
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Active Filters */}
        {!isVehicle && !isProperty && (
          <ActiveFilters 
            currentCategory={currentCategory}
            selectedLocation={selectedLocation}
            condition={condition}
            onNavigate={navigate}
            onLocationClear={() => setSelectedLocation('')}
            onConditionClear={() => setCondition('all')}
            onClearAll={clearFilters}
          />
        )}
        
        <div className={`grid grid-cols-1 ${isProperty ? '' : 'lg:grid-cols-4'} gap-6`}>
          {/* Sidebar Filters - Desktop (hide for property) */}
          {!isProperty && (
            <div className="hidden lg:block">
              {isVehicle ? (
                <VehicleFilters
                  vehicleType={getVehicleType()}
                  onSearch={handleVehicleSearch}
                  onFilterChange={handleVehicleFilterChange}
                  initialFilters={vehicleFilters}
                  resultCount={totalCount}
                  className="sticky top-20"
                />
              ) : (
                <Card className="p-4 sticky top-20">
                  <div className="flex items-center space-x-2 mb-4">
                    <SlidersHorizontal className="h-5 w-5 text-blue-600" />
                    <h2 className="text-lg font-semibold">Filters</h2>
                  </div>
                  <FilterSection />
                </Card>
              )}
            </div>
          )}

          {/* Main Content */}
          <div className={isProperty ? '' : 'lg:col-span-3'}>
            {/* Toolbar */}
            <div className="flex items-center justify-between mb-4 bg-white p-3 rounded-lg shadow-sm">
              <div className="flex items-center space-x-2">
                {/* Mobile Filter - hide for property (uses its own mobile filters) */}
                {!isProperty && (
                  <MobileFilterSheet
                    open={mobileFilterOpen}
                    onOpenChange={setMobileFilterOpen}
                    title="Filters"
                  >
                    {isVehicle ? (
                      <VehicleFilters
                        vehicleType={getVehicleType()}
                        onSearch={handleVehicleSearch}
                        onFilterChange={handleVehicleFilterChange}
                        initialFilters={vehicleFilters}
                        resultCount={totalCount}
                      />
                    ) : (
                      <div className="p-4">
                        <FilterSection />
                      </div>
                    )}
                  </MobileFilterSheet>
                )}

                {/* Sort Select - hide for property (handled by PropertyFilters) */}
                {!isProperty && (
                  <SortSelector
                    value={sortBy}
                    onChange={(value) => {
                      setSortBy(value);
                      if (isVehicle) {
                        fetchListings(vehicleFilters);
                      }
                    }}
                  />
                )}

                {/* Ad count + active sort - moved here from the purple banner */}
                {!isProperty && (
                  <p
                    className="hidden sm:flex items-center gap-2 text-sm text-gray-600 ml-1"
                    data-testid="ads-count-info"
                  >
                    <span className="font-semibold text-gray-900">
                      {totalCount.toLocaleString()}
                    </span>
                    <span>
                      ads{slug && slug !== 'all' ? ` in ${getCategoryDisplayName(slug)}` : ' found'}
                    </span>
                  </p>
                )}
              </div>

              <ViewModeToggle
                viewMode={viewMode}
                onViewModeChange={setViewMode}
              />
            </div>

            {/* Listings Grid */}
            {loading ? (
              <ListingsLoading />
            ) : error ? (
              <ListingsError error={error} onRetry={fetchListings} />
            ) : isCommunityLocalLifeParent ? (
              /* Special handling for Community & Local Life parent category */
              <CommunityLocalLifeSection />
            ) : isProperty && propertyViewMode === 'map' ? (
              /* Property Map View */
              <PropertyMapView 
                listings={listings}
                country={country}
                onListView={() => setPropertyViewMode('list')}
              />
            ) : isProperty && propertyViewMode === 'heat' ? (
              /* Property Heat Map View */
              <PropertyHeatMap 
                listings={listings}
                country={country}
                onListView={() => setPropertyViewMode('list')}
              />
            ) : isProperty && propertyViewMode === 'draw' ? (
              /* Property Draw on Map View */
              <DrawableMap 
                listings={listings}
                country={country}
                onListView={() => setPropertyViewMode('list')}
                onAreaChange={(area) => {
                  if (area) {
                    // Update listings to show only those in drawn area
                    console.log(`Found ${area.matchCount} properties in drawn area`);
                  }
                }}
              />
            ) : isProperty && propertyViewMode === 'list' ? (
              /* NEW: Property Search Results with Context Panel */
              <PropertySearchResults
                listings={listings}
                loading={loading}
                error={error}
                totalCount={totalCount}
                filters={effectivePropertyFilters}
                sortBy={sortBy}
                onSortChange={setSortBy}
                onFilterRemove={(key) => {
                  if (isProperty) {
                    hookRemoveFilter(key);
                  } else {
                    setPropertyFilters(prev => ({ ...prev, [key]: key === 'price' ? {} : 'any' }));
                  }
                }}
                onClearFilters={() => {
                  if (isProperty) {
                    hookClearAllFilters();
                  } else {
                    setPropertyFilters({});
                  }
                }}
                searchCenter={effectivePropertyFilters?.searchCenter}
                hoverZoomSettings={hoverZoomSettings}
              />
            ) : listings.length > 0 ? (
              <>
                {/* Recent Searches for Property (only show in list view) */}
                {isProperty && propertyViewMode === 'list' && (
                  <div className="mb-4">
                    <RecentSearches 
                      maxItems={3}
                      onSearchSelect={(search) => {
                        console.log('Selected search:', search);
                      }}
                    />
                  </div>
                )}
                
                {/* Featured Ads Section */}
                <FeaturedAdsSection 
                  listings={listings} 
                  viewMode={viewMode}
                  showCompare={isProperty}
                  compareIds={compareIds}
                  onCompareToggle={handleCompareToggle}
                />

                {/* Regular Ads Section */}
                <RegularAdsSection 
                  listings={listings} 
                  viewMode={viewMode} 
                  hasFeatured={listings.some(l => l.featured)}
                  showCompare={isProperty}
                  compareIds={compareIds}
                  onCompareToggle={handleCompareToggle}
                />
                
                {/* Event Service Providers Section */}
                {isEventService && (
                  <EventProvidersSection 
                    categorySlug={slug}
                    country={country}
                    maxProviders={4}
                  />
                )}
                
                {/* Community Service Providers Section */}
                {isCommunity && (
                  <CommunityServicesSection 
                    categorySlug={slug}
                    country={country}
                    maxProviders={4}
                  />
                )}
                
                {/* Books & Media Service Providers Section */}
                {isBooksMediaCategory(slug) && (
                  <BooksMediaServicesSection 
                    categorySlug={slug}
                    country={country}
                    maxProviders={4}
                  />
                )}
                
                {/* Services Section for Fashion Categories */}
                {isFashion && (
                  <div className="mt-8 space-y-6">
                    <ServicesSection 
                      category={getServiceCategory()}
                      maxProviders={4}
                      viewMode="grid"
                      showMobilePill={true}
                    />
                    {/* Laundry Services for Clothing Categories */}
                    {isClothing && (
                      <ServicesSection 
                        category="laundry-dryclean"
                        maxProviders={4}
                        viewMode="grid"
                        showMobilePill={false}
                      />
                    )}
                    {/* Jewellery Financial Services (Insurance & Loans) */}
                    {isJewellery && (
                      <JewelleryFinanceServices country={country} />
                    )}
                  </div>
                )}
                
                {/* Sports, Fitness & Hobbies Services Section */}
                {isSports && (
                  <div className="mt-8 space-y-6">
                    <SportsServicesSection 
                      subcategory={getSportsSubcategory()}
                      maxProviders={4}
                      viewMode="grid"
                      country={country}
                      showDualCard={true}
                    />
                  </div>
                )}
                
                {/* Services & Trades Section */}
                {isServicesTrades && (
                  <div className="mt-8 space-y-6">
                    {/* Emergency Services Banner */}
                    <EmergencyServicesBanner 
                      country={country}
                      onViewAll={() => navigate('/category/services-trades?emergency=true')}
                    />
                    
                    {/* Show all subcategories with services if on main services-trades page */}
                    {slug === 'services-trades' && !getTradesSubcategory() && (
                      <>
                        <div className="mb-8">
                          <AllSubcategoriesServicesDisplay country={country} />
                        </div>
                        
                        {/* Featured Providers on main page */}
                        <FeaturedTradeProviders country={country} />
                      </>
                    )}
                    
                    {/* Show services list for specific subcategory */}
                    {getTradesSubcategory() && (
                      <SubcategoryServicesDisplay 
                        subcategorySlug={getTradesSubcategory()}
                        country={country}
                      />
                    )}
                    
                    {/* Show providers for specific subcategory */}
                    {getTradesSubcategory() && (
                      <TradesServicesSection 
                        subcategorySlug={getTradesSubcategory()}
                        country={country}
                      />
                    )}
                  </div>
                )}
                
                {/* Business & Industry Section */}
                {isBusinessIndustry && (
                  <div className="mt-8 space-y-6">
                    {/* RFQ Entry Button for applicable subcategories */}
                    {isRFQEnabledSubcategory && (
                      <div className="flex items-center justify-between p-4 bg-purple-50 border border-purple-200 rounded-lg">
                        <div>
                          <h3 className="font-semibold text-purple-800">Need bulk supplies?</h3>
                          <p className="text-sm text-purple-600">Get quotes from multiple verified suppliers</p>
                        </div>
                        <RFQEntryButton 
                          subcategory={{ slug: getBusinessSubcategory(), subcategory_name: slug?.replace(/-/g, ' ') }}
                          country={country}
                        />
                      </div>
                    )}
                    
                    {/* Show services for specific subcategory */}
                    {getBusinessSubcategory() && (
                      <BusinessServicesDisplay 
                        subcategorySlug={getBusinessSubcategory()}
                        country={country}
                      />
                    )}
                  </div>
                )}

                {/* Food & Agriculture Section */}
                {isFoodAgriculture && (
                  <div className="mt-8 space-y-6">
                    {/* What's In Season Section */}
                    <WhatsInSeasonSection 
                      country={country}
                      onFilterByTags={(tags, subcategory) => {
                        // Navigate to subcategory with tag filter
                        if (subcategory) {
                          navigate(`/category/food-agriculture/${subcategory}?tags=${tags.join(',')}`);
                        }
                      }}
                      className="mb-6"
                    />
                    
                    {/* RFQ Entry Button for applicable subcategories */}
                    {isFoodAgriRFQEnabled && (
                      <div className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-lg">
                        <div>
                          <h3 className="font-semibold text-green-800">Need bulk agricultural supplies?</h3>
                          <p className="text-sm text-green-600">Get quotes from multiple verified suppliers</p>
                        </div>
                        <RFQEntryButton 
                          subcategory={{ slug: getFoodAgriSubcategory(), subcategory_name: slug?.replace(/-/g, ' ') }}
                          country={country}
                        />
                      </div>
                    )}
                    
                    {/* Show services for specific subcategory */}
                    {getFoodAgriSubcategory() && (
                      <FoodAgricultureServicesDisplay 
                        subcategorySlug={getFoodAgriSubcategory()}
                        country={country}
                      />
                    )}
                  </div>
                )}

                {/* Baby & Kids Items Section - Services */}
                {isBabyKids && (
                  <div className="mt-8">
                    <BabyKidsServicesDisplay />
                  </div>
                )}

                {/* Musical Instruments Services Section */}
                {isMusicalInstruments && (
                  <div className="mt-8 space-y-6" data-testid="music-services-section">
                    <ServicesSection 
                      category={getMusicSubcategory() || slug}
                      maxProviders={4}
                      viewMode="grid"
                      showMobilePill={true}
                      country={country}
                    />
                  </div>
                )}
              </>
            ) : (
              <>
                <ListingsEmpty onClearFilters={clearFilters} />
                
                {/* Community Services Section - show even without listings */}
                {isCommunity && (
                  <div className="space-y-6">
                    <CommunityServicesSection 
                      categorySlug={slug}
                      country={country}
                      maxProviders={4}
                    />
                  </div>
                )}
                
                {/* Books & Media Services Section - show even without listings */}
                {isBooksMediaCategory(slug) && (
                  <div className="space-y-6">
                    <BooksMediaServicesSection 
                      categorySlug={slug}
                      country={country}
                      maxProviders={4}
                    />
                  </div>
                )}
                
                {/* Services Section for Fashion Categories - show even without listings */}
                {isFashion && (
                  <div className="space-y-6">
                    <ServicesSection 
                      category={getServiceCategory()}
                      maxProviders={4}
                      viewMode="grid"
                      showMobilePill={true}
                    />
                    {/* Laundry Services for Clothing Categories */}
                    {isClothing && (
                      <ServicesSection 
                        category="laundry-dryclean"
                        maxProviders={4}
                        viewMode="grid"
                        showMobilePill={false}
                      />
                    )}
                    {/* Jewellery Financial Services (Insurance & Loans) */}
                    {isJewellery && (
                      <JewelleryFinanceServices country={country} />
                    )}
                  </div>
                )}
                
                {/* Sports, Fitness & Hobbies Services Section - show even without listings */}
                {isSports && (
                  <div className="space-y-6">
                    <SportsServicesSection 
                      subcategory={getSportsSubcategory()}
                      maxProviders={4}
                      viewMode="grid"
                      country={country}
                      showDualCard={true}
                    />
                  </div>
                )}
                
                {/* Services & Trades Section - show even without listings */}
                {isServicesTrades && (
                  <div className="space-y-6">
                    {/* Emergency Services Banner */}
                    <EmergencyServicesBanner 
                      country={country}
                      onViewAll={() => navigate('/category/services-trades?emergency=true')}
                    />
                    
                    {/* Show all subcategories with services if on main services-trades page */}
                    {slug === 'services-trades' && !getTradesSubcategory() && (
                      <div className="mb-8">
                        <AllSubcategoriesServicesDisplay country={country} />
                      </div>
                    )}
                    
                    {/* Show services list for specific subcategory */}
                    {getTradesSubcategory() && (
                      <SubcategoryServicesDisplay 
                        subcategorySlug={getTradesSubcategory()}
                        country={country}
                      />
                    )}
                    
                    {/* Show providers for specific subcategory */}
                    {getTradesSubcategory() && (
                      <TradesServicesSection 
                        subcategorySlug={getTradesSubcategory()}
                        country={country}
                      />
                    )}
                  </div>
                )}

                {/* Food & Agriculture Section - show even without listings */}
                {isFoodAgriculture && (
                  <div className="space-y-6">
                    {/* RFQ Entry Button for applicable subcategories */}
                    {isFoodAgriRFQEnabled && (
                      <div className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-lg">
                        <div>
                          <h3 className="font-semibold text-green-800">Need bulk agricultural supplies?</h3>
                          <p className="text-sm text-green-600">Get quotes from multiple verified suppliers</p>
                        </div>
                        <RFQEntryButton 
                          subcategory={{ slug: getFoodAgriSubcategory(), subcategory_name: slug?.replace(/-/g, ' ') }}
                          country={country}
                        />
                      </div>
                    )}
                    
                    {/* Show services for specific subcategory */}
                    {getFoodAgriSubcategory() && (
                      <FoodAgricultureServicesDisplay 
                        subcategorySlug={getFoodAgriSubcategory()}
                        country={country}
                      />
                    )}
                  </div>
                )}

                {/* Education & Tuition Section */}
                {isEducation && (
                  <EducationTuitionServicesDisplay 
                    country={country}
                    subcategorySlug={slug}
                  />
                )}

                {/* Baby & Kids Items Section */}
                {isBabyKids && (
                  <BabyKidsServicesDisplay />
                )}

                {/* Musical Instruments Services Section - show even without listings */}
                {isMusicalInstruments && (
                  <div className="space-y-6" data-testid="music-services-section-empty">
                    <ServicesSection 
                      category={getMusicSubcategory() || slug}
                      maxProviders={4}
                      viewMode="grid"
                      showMobilePill={true}
                      country={country}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      
      {/* Compare Properties Modal */}
      {isProperty && (
        <>
          <CompareProperties
            isOpen={showCompareModal}
            onClose={() => setShowCompareModal(false)}
            selectedIds={compareIds}
            onRemove={(id) => setCompareIds(prev => prev.filter(i => i !== id))}
            currency={country === 'UK' ? '£' : 'Rs.'}
          />
          
          {/* Floating Compare Bar */}
          <CompareBar
            selectedCount={compareIds.length}
            maxCount={MAX_COMPARE}
            onCompare={() => setShowCompareModal(true)}
            onClear={() => setCompareIds([])}
          />
        </>
      )}
    </div>
  );
};

export default CategoryPage;
